import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../api';

// Category table: name, Bruto multiplier, active, display order.
// Multiplier changes apply to new entries / Neto edits only — history is never recalculated.
export default function CategoriesSection({ loggedInUser }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState('');
  const [newCat, setNewCat] = useState({ name: '', bruto_multiplier: '1.20', sort_order: '' });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/categories`, { params: { all: 1 } });
      setRows(res.data);
      setDraft({});
    } catch { toast.error('Could not load categories.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const value = (row, key) => (draft[row.id] && draft[row.id][key] !== undefined ? draft[row.id][key] : row[key]);
  const edit = (row, key, v) => setDraft((d) => ({ ...d, [row.id]: { ...d[row.id], [key]: v } }));

  const call = async (fn) => {
    setError('');
    try { await fn(); await load(); return true; } catch (err) {
      setError(err.response?.data?.error || 'Could not save.');
      return false;
    }
  };
  const save = (row) => call(() => axios.put(`${API_BASE_URL}/api/categories/${row.id}`, { username: loggedInUser, ...draft[row.id] }));
  const toggle = (row) => call(() => axios.put(`${API_BASE_URL}/api/categories/${row.id}`, { username: loggedInUser, is_active: row.is_active ? 0 : 1 }));
  const add = async () => {
    const ok = await call(() => axios.post(`${API_BASE_URL}/api/categories`, {
      username: loggedInUser,
      name: newCat.name,
      bruto_multiplier: Number(newCat.bruto_multiplier),
      sort_order: Number(newCat.sort_order) || rows.length + 1,
    }));
    if (ok) setNewCat({ name: '', bruto_multiplier: '1.20', sort_order: '' });
  };

  const input = 'border p-1 rounded text-black bg-white dark:bg-black dark:text-white';
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 max-w-2xl">
        The multiplier applies to entries from 01/09/2026. Changing a multiplier does not recalculate existing entries, only new entries and Neto edits.
        A deactivated category disappears from the choices for new entries; existing entries stay.
      </p>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2" role="alert">{error}</p>}
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            <th className="border p-2 text-left">Name</th>
            <th className="border p-2 text-left">Bruto multiplier</th>
            <th className="border p-2 text-left">Order</th>
            <th className="border p-2 text-left">Active</th>
            <th className="border p-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={r.is_active ? '' : 'opacity-60'}>
              <td className="border p-2"><input value={value(r, 'name')} onChange={(e) => edit(r, 'name', e.target.value)} className={input} /></td>
              <td className="border p-2"><input type="number" step="0.01" min="0.01" max="10" value={value(r, 'bruto_multiplier')}
                onChange={(e) => edit(r, 'bruto_multiplier', e.target.value)} className={`${input} w-24`} /></td>
              <td className="border p-2"><input type="number" step="1" value={value(r, 'sort_order')}
                onChange={(e) => edit(r, 'sort_order', e.target.value)} className={`${input} w-20`} /></td>
              <td className="border p-2">
                <button onClick={() => toggle(r)} className={`px-2 py-1 rounded text-white ${r.is_active ? 'bg-green-600' : 'bg-gray-500'}`}>
                  {r.is_active ? 'Yes' : 'No'}
                </button>
              </td>
              <td className="border p-2">
                <button onClick={() => save(r)} disabled={!draft[r.id]}
                  className="bg-indigo-600 text-white px-2 py-1 rounded disabled:opacity-40">Save</button>
              </td>
            </tr>
          ))}
          <tr>
            <td className="border p-2"><input value={newCat.name} placeholder="New category"
              onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className={input} /></td>
            <td className="border p-2"><input type="number" step="0.01" value={newCat.bruto_multiplier}
              onChange={(e) => setNewCat({ ...newCat, bruto_multiplier: e.target.value })} className={`${input} w-24`} /></td>
            <td className="border p-2"><input type="number" step="1" value={newCat.sort_order}
              onChange={(e) => setNewCat({ ...newCat, sort_order: e.target.value })} className={`${input} w-20`} /></td>
            <td className="border p-2" />
            <td className="border p-2"><button onClick={add} className="bg-indigo-500 text-white px-2 py-1 rounded">Add</button></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
